
import { Vue, Component, Prop } from "vue-property-decorator";
import BookmarkUser from "@/apollo/smartMatch/BookmarkUser.gql";
import UnbookmarkUser from "@/apollo/smartMatch/UnbookmarkUser.gql";
import BookmarkUserByAdmin from "@/apollo/smartMatch/BookmarkUserByAdmin.gql";
import UnbookmarkUserByAdmin from "@/apollo/smartMatch/UnbookmarkUserByAdmin.gql";
import INSERT_USER_FAVORITE_ONE from "@/apollo/user/InsertUserFavoriteOne.gql";
import DELETE_USER_FAVORITE_BY_PK from "@/apollo/user/DeleteUserFavoriteByPk.gql";

interface ProfileResult {
  id: string;
  userId: string;
  name: string;
  title: string;
  rating: number;
  matchPercentage: number;
  hourlyRate: string;
  avatar: string;
  favorited: boolean;
  favoriteId?: string;
  bookmarked: boolean;
  languages: string[];
  distance: string;
  hasVehicle: boolean;
  availableHours: number;
  clientWorkCount: number;
  credentials: string[];
  skills: string[];
  softSkills: string[];
  certifications: string[];
}

@Component({
  components: {},
})
export default class SmartMatchProfileCard extends Vue {
  @Prop({ type: Object, required: true }) readonly profile!: ProfileResult;
  @Prop({ type: String, required: false, default: null }) readonly managerId!: string | null;
  @Prop({ type: String, required: false, default: null }) readonly adminId!: string | null;
  @Prop({ type: String, required: true }) readonly projectId!: string;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;

  private isBookmarkLoading = false;
  private isFavoriteLoading = false;

  get matchPercentageChipClass(): string {
    const percentage = this.profile.matchPercentage;
    
    if (percentage >= 80) {
      // 80-100% = green - keep it the same
      return 'bg-green-100 text-green-800';
    } else if (percentage >= 60) {
      // 60-79% = yellow
      return 'bg-yellow-100 text-yellow-800';
    } else {
      // 0-59% = red
      return 'bg-red-600 text-white';
    }
  }

  get matchPercentageChipStyle(): object {
    const percentage = this.profile.matchPercentage;
    
    if (percentage >= 80) {
      // 80-100% = green - keep it the same (use default Tailwind classes)
      return {};
    } else if (percentage >= 60) {
      // 60-79% = yellow
      return {
        color: '#733e0a',
        backgroundColor: '#fef9c2'
      };
    } else {
      // 0-59% = red
      return {
        color: '#82181a',
        backgroundColor: '#ffe2e2'
      };
    }
  }

  async toggleFavorite() {
    if (this.isFavoriteLoading) return;
    
    this.isFavoriteLoading = true;
    const newState = !this.profile.favorited;
    
    try {
      if (newState) {
        // Add to favorites
        const result = await this.$apollo.mutate({
          mutation: INSERT_USER_FAVORITE_ONE,
          variables: {
            object: {
              favorited_by_id: this.currentUserId,
              favorited_id: this.profile.userId
            },
            id: this.currentUserId
          }
        });
        console.log(`💖 Added ${this.profile.name} to favorites`);
      } else {
        // Remove from favorites using the stored favoriteId
        if (this.profile.favoriteId) {
          await this.$apollo.mutate({
            mutation: DELETE_USER_FAVORITE_BY_PK,
            variables: {
              favoriteEntryId: this.profile.favoriteId
            }
          });
          console.log(`💔 Removed ${this.profile.name} from favorites`);
        } else {
          console.log('⚠️ No favorite ID found, cannot remove from favorites');
        }
      }
      
      // Emit the change to parent component
      this.$emit('favorite-toggled', this.profile.id, newState);
      
    } catch (error) {
      console.log('❌ Error toggling favorite:', error);
    } finally {
      this.isFavoriteLoading = false;
    }
  }

  async toggleBookmark() {
    if (this.isBookmarkLoading) return;
    
    this.isBookmarkLoading = true;
    const newState = !this.profile.bookmarked;
    
    try {
      if (newState) {
        // Bookmark the user
        if (this.adminId) {
          // Admin user
          await this.$apollo.mutate({
            mutation: BookmarkUserByAdmin,
            variables: {
              admin_id: this.adminId,
              project_id: this.projectId,
              user_id: this.profile.userId,
              match_percentage: this.profile.matchPercentage
            }
          });
        } else if (this.managerId) {
          // Manager user
          await this.$apollo.mutate({
            mutation: BookmarkUser,
            variables: {
              manager_id: this.managerId,
              project_id: this.projectId,
              user_id: this.profile.userId,
              match_percentage: this.profile.matchPercentage
            }
          });
        } else {
          throw new Error('No manager ID or admin ID provided');
        }
      } else {
        // Unbookmark the user
        if (this.adminId) {
          // Admin user
          await this.$apollo.mutate({
            mutation: UnbookmarkUserByAdmin,
            variables: {
              admin_id: this.adminId,
              project_id: this.projectId,
              user_id: this.profile.userId
            }
          });
        } else if (this.managerId) {
          // Manager user
          await this.$apollo.mutate({
            mutation: UnbookmarkUser,
            variables: {
              manager_id: this.managerId,
              project_id: this.projectId,
              user_id: this.profile.userId
            }
          });
        } else {
          throw new Error('No manager ID or admin ID provided');
        }
      }
      
      // Emit the change to parent component
      this.$emit('bookmark-toggled', this.profile.id, newState);
      
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      // Could show user-friendly error message here
    } finally {
      this.isBookmarkLoading = false;
    }
  }

  chatClicked() {
    this.$emit('chat-clicked', this.profile.id);
  }

  onAvatarError(event: Event) {
    console.error('Avatar failed to load:', this.profile.avatar);
    // The v-if will handle showing the SVG fallback if avatar is empty
  }

  getRandomChipColor(type: string, index: number): string {
    // Fixed colors for each category
    switch (type) {
      case 'credential':
        return 'text-white';
      case 'certification':
        return 'text-white'; // Darkest blue background
      case 'skill':
        return 'text-white'; // Medium blue background
      case 'softSkill':
        return 'text-blue-800'; // Lightest blue background, dark text for contrast
      default:
        return 'text-gray-900';
    }
  }
}
